import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { LOCALE_ID } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpModule, Http, RequestOptions } from '@angular/http';
import { AuthHttp, AuthConfig } from 'angular2-jwt';
import { APP_BASE_HREF } from '@angular/common';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { PagesModule } from './modules/employee/pages/pages.module';
import { GlobalState } from './global.state';
import { NgaModule } from './theme/nga.module';

import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { DndModule } from 'ng2-dnd';

import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslatePoHttpLoader } from '@biesbjerg/ngx-translate-po-http-loader';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { RouteService } from './modules/employee/service/route/route.service';
import { ConfirmationService } from 'primeng/primeng';

// Authentication
import { AuthLoginGuard } from './modules/employee/service/auth-guard-login/auth-login.guard';
import { AuthLoginCandidateGuard } from './modules/candidate/services/auth-login-candidate.guard';

// Login
import { LoginService } from './modules/employee/service/login/login.service';
import { LoginCandidateService } from './modules/employee/service/login-candidate/login-candidate.service';

// Account
import { AccountService } from './modules/employee/service/account/account.service';

// Upload files
import { UploadFilesService } from './modules/employee/service/upload-files/upload-files.service';
import { UploadFilesCandidateService } from './modules/candidate/services/upload-files-candidate/upload-files-candidate.service';

// User
import { UserService } from './modules/employee/service/user/user.service';

// Dashboard
import { DashboardUrlService } from './modules/employee/service/dashboard-url/dashboard-url.service';
import { DashboardSorttimeService } from './modules/employee/service/dashboard-sorttime/dashboard-sorttime.service';

// Candidate
import { CandidateService } from './modules/employee/service/candidate/candidate.service';
import { CandidateCommentService } from './modules/employee/service/candidate-comment/candidate-comment.service';

// Candidate portal
import { ContactCandidateService } from './modules/candidate/services/contact-candidate/contact-candidate.service';
import { CandidateVacanciesService } from './modules/candidate/services/candidate-vacancies/candidate-vacancies.service';
import { CandidateApplicationsService } from './modules/candidate/services/candidate-applications/candidate-applications.service';
import { CandidateDisabilitiesService } from './modules/disability/services/candidate-disabilities.service';

// Company, venues and deparments
import { CompanyService } from './modules/employee/service/company/company.service';
import { VenueService } from './modules/employee/service/venue/venue.service';
import { DepartmentService } from './modules/employee/service/department/department.service';

// Employees, roles and permissions
import { EmployeePositionService } from './modules/employee/service/employee-position/employee-position.service';
import { EmployeeService } from './modules/employee/service/employee/employee.service';
import { RoleService } from './modules/employee/service/role/role.service';
import { PermissionService } from './modules/employee/service/permission/permission.service';
import { HasPermissionService } from './modules/employee/service/has-permission/has-permission.service';

// Customer guides
import { CustomerGuideService } from './modules/employee/service/customer-guide/customer-guide.service';
import { CustomerGuideHseDocumentsService } from './modules/employee/service/customer-guide-hse-document/customer-guide-hse-document.service';
import { CustomerGuideJustificationService } from './modules/employee/service/customer-guide-justification/customer-guide-justification.service';

// Medical cases
import { MedicalCasesService } from './modules/employee/service/medical-cases/medical-cases.service';

// Medical exams
import { MedicalExamsService } from './modules/employee/service/medical-exams/medical-exams.service';
// Profile survey
import { ProfileSurveyService } from './modules/employee/service/profile-survey/profile-survey.service';
import { ProfileSurveyObservationService } from './modules/employee/service/profile-survey-observation/profile-survey-observation.service';
import { ChecklistProfileSurveyService } from './modules/employee/service/checklist-profile-survey/checklist-profile-survey.service';
import { ListDocumentProfileSurveyService } from './modules/employee/service/list-document-profile-survey/list-document-profile-survey.service';
import { DocumentProfileSurveyService } from './modules/employee/service/document-profile-survey/document-profile-survey.service';

// Stations and events
import { StationService } from './modules/employee/service/station/station.service';
import { EventService } from './modules/employee/service/event/event.service';
import { NewnessService } from './modules/employee/service/newness/newness.service';

// Schedule
import { ScheduleEmployeeService } from './modules/employee/service/schedule-employee/scheduleEmployee.service';

// Requisitions
import { RequisitionService } from './modules/employee/service/requisition/requisition.service';
import { RequisitionJustificationService } from './modules/employee/service/requisition-justification/requisition-justification.service';
import { RequisitionTaskService } from './modules/employee/service/requisition-task/requisition-task.service';

// Citation and recruitments
import { CitationService } from './modules/employee/service/citation/citation.service';
import { RecruitmentService } from './modules/employee/service/recruitment/recruitment.service';

import { AttendanceJustificationService } from './modules/employee/service/attendance-justification/attendance-justification.service';
import { OpeningStationService } from './modules/employee/service/opening-station/opening-station.service';

// Synergo Comunicación Alternativa
import { SynergoCitationService } from './modules/employee/service/synergo-citation/synergo-citation.service';
import { SynergoRequisitionService } from './modules/employee/service/synergo-requisition/synergo-requisition.service';

// Education and work experience
import { EducationService } from './modules/employee/service/education/education.service';
import { WorkExperienceService } from './modules/employee/service/work-experience/work-experience.service';

// Development Requests
import { DevelopmentRequestService } from './modules/employee/service/development-request/development-request.service';

// System Requests
import { SystemRequestService } from './modules/employee/service/system-request/system-request.service';
import { SystemRequestTypesService } from './modules/employee/service/system-request-types/system-request-types.service';
import { SystemRequestTypificationsService } from './modules/employee/service/system-request-typifications/system-request-typifications.service';

// PQRSF
import { PqrService } from './modules/employee/service/pqr/pqr.service';
import { PqrTypificationService } from './modules/employee/service/pqr-typification/pqr-typification.service';

// Banks, radication SS and findings
import { BankService } from './modules/employee/service/bank/bank.service';
import { BankBookService } from './modules/employee/service/bank-book/bank-book.service';
import { RadicationSSService } from './modules/employee/service/radicationSS/radicationSS.service';
import { FindingService } from './modules/employee/service/finding/finding.service';

// Archives and directory
import { ArchiveService } from './modules/employee/service/archive/archive.service';
import { DirectoryService } from './modules/employee/service/directory/directory.service';

// Worker cares
import { WorkerCareService } from './modules/employee/service/worker-care/worker-care.service';

// Gestion SACs
import { GestionSACService } from './modules/employee/service/gestionSAC/gestionSAC.service';

// Minute and daily visits
import { MinuteService } from './modules/employee/service/minute/minute.service';
import { DailyVisitService } from './modules/employee/service/daily-visit/daily-visit.service';

// Places
import { CountryService } from './modules/employee/service/country/country.service';
import { AdministrativeDivisionService } from './modules/employee/service/administrative-division/administrative-division.service';
import { MunicipalityService } from './modules/employee/service/municipality/municipality.service';

// Height and professions
import { HeightService } from './modules/employee/service/height/height.service';
import { ProfessionService } from './modules/employee/service/profession/profession.service';

// Contracts and folders
import { ContractService } from './modules/employee/service/contract/contract.service';
import { FolderShareService } from './modules/employee/service/folder-share/folder-share.service';

// Tips or Información de interés
import { TipService } from './modules/employee/service/tip/tip.service';

// user-idle
import { UserIdleService } from './modules/employee/service/user-idle/user-idle.service';

// Inspections
import { InspectionService } from './modules/employee/service/inspection/inspection.service';
import { InspectionEquipmentService } from './modules/employee/service/inspection-equipment/inspection-equipment.service';

import { TheftService } from './modules/employee/service/theft/theft.service';

// System inventory requests
import { SystemsInventoryRequestService } from './modules/employee/service/systems-inventory-request/systems-inventory-request.service';

// Offer area
import { OfferAreaService } from './modules/employee/service/offer-area/offer-area.service';

// Offer Primary Service
import { OfferPrimayAreaService } from './modules/employee/service/offer-primary-area/offer-primary-area.service';

// Modules
import { CandidateModule } from './modules/candidate/candidate.module';
import { MultiLoginModule } from './modules/multi-login/multi-login.module';
import { DisabilityModule } from './modules/disability/disability.module';

// Pipes
import { ControlRequiredPipe } from './modules/employee/pipe/controlRequired/controlRequired.pipe';

import { SliderModule } from 'primeng/primeng';
import { MedicalCasesEventService } from './modules/employee/service/medical-cases-event/medical-cases-event.service';
import { MedicalCaseEventCandidateSharedService } from './modules/employee/service/medical-case-event-candidate/medical-case-event-candidate-shared.service';
import { UploadFilesMedicalCaseEventService } from './modules/employee/service/medical-cases-event/upload-files-medical-case-event.service';

import { DiagnosticService } from './modules/custom-autocomplete/service/diagnostic/diagnostic.service';
import { CandidateDisabilityService } from './modules/custom-autocomplete/service/candidate-disability/candidate-disability.service';
import { VacationService } from './modules/employee/service/vacation/vacation.service';
import { LaboratoryService } from './modules/employee/service/laboratory/laboratory.service';


export function authHttpServiceFactory(http: Http, options: RequestOptions) {
  return new AuthHttp(new AuthConfig({}), http, options);
}

export function createTranslateLoader(http: Http) {
  return new TranslatePoHttpLoader(http, '/assets/i18n/', '.po');
}

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    BrowserModule,
    SliderModule,
    ReactiveFormsModule,
    FormsModule,
    HttpModule,
    PagesModule,
    CandidateModule,
    MultiLoginModule,
    DisabilityModule,
    AppRoutingModule,
    NgaModule.forRoot(),
    StoreDevtoolsModule.instrument({
      maxAge: 10 // number of states to retain
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [Http]
      }
    }),
    InfiniteScrollModule,
    DndModule.forRoot(),
  ],
  providers: [
    {
      provide: AuthHttp,
      useFactory: authHttpServiceFactory,
      deps: [Http, RequestOptions]
    },
    { provide: LOCALE_ID, useValue: "ES-US" },
    { provide: APP_BASE_HREF, useValue: '/' },
    GlobalState,
    ConfirmationService,
    LoginService,
    LoginCandidateService,
    AccountService,
    UploadFilesService,
    UploadFilesCandidateService,
    UserService,
    DashboardUrlService,
    DashboardSorttimeService,
    ContactCandidateService,
    CandidateVacanciesService,
    CandidateApplicationsService,
    DiagnosticService,
    CandidateDisabilityService,
    CompanyService,
    VenueService,
    DepartmentService,
    EmployeePositionService,
    EmployeeService,
    RoleService,
    PermissionService,
    CustomerGuideService,
    CustomerGuideHseDocumentsService,
    MedicalCasesService,
    MedicalCasesEventService,
    MedicalCaseEventCandidateSharedService,
    UploadFilesMedicalCaseEventService,
    HasPermissionService,
    ProfileSurveyService,
    ProfileSurveyObservationService,
    StationService,
    EventService,
    NewnessService,
    ScheduleEmployeeService,
    RequisitionJustificationService,
    AttendanceJustificationService,
    OpeningStationService,
    CitationService,
    SynergoCitationService,
    RequisitionService,
    SynergoRequisitionService,
    CandidateService,
    EducationService,
    WorkExperienceService,
    CandidateCommentService,
    DevelopmentRequestService,
    SystemRequestService,
    SystemRequestTypesService,
    SystemRequestTypificationsService,
    PqrService,
    PqrTypificationService,
    BankService,
    BankBookService,
    RadicationSSService,
    FindingService,
    ArchiveService,
    DirectoryService,
    WorkerCareService,
    GestionSACService,
    MinuteService,
    DailyVisitService,
    ChecklistProfileSurveyService,
    FolderShareService,
    ListDocumentProfileSurveyService,
    RequisitionTaskService,
    RecruitmentService,
    CountryService,
    AdministrativeDivisionService,
    MunicipalityService,
    HeightService,
    ContractService,
    AuthLoginGuard,
    AuthLoginCandidateGuard,
    TipService,
    RouteService,
    CustomerGuideJustificationService,
    InspectionService,
    InspectionEquipmentService,
    TheftService,
    DocumentProfileSurveyService,
    ProfessionService,
    SystemsInventoryRequestService,
    OfferAreaService,
    OfferPrimayAreaService,
    UserIdleService,
    ControlRequiredPipe,
    CandidateDisabilitiesService,
    VacationService,
    MedicalExamsService,
    LaboratoryService
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }



// WEBPACK FOOTER //
// D:/inetpub/wwwroot/kaizen-ng/src/app/app.module.ts